import { Info, InfoIcon, Upload } from 'lucide-react';
import { ChangeEvent, useRef, useState } from 'react';

import Button from '../Common/Button';
import CommonDialog from '../Common/Dialog';

const MAX_SIZE_MB = 10;
const ACCEPTED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];
const ACCEPTED_EXTENSIONS = ['.pdf', '.doc', '.docx'];

const BoostATSModal = ({
  isOpen,
  close,
  onSubmit
}: {
  isOpen: boolean;
  close: () => void;
  onSubmit: (data: {
    file: File;
    jobRole: string;
    jobDescription: string;
  }) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [jobRole, setJobRole] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showHint, setShowHint] = useState(false);

  const validateFile = (selected: File): string => {
    const ext = selected.name.toLowerCase();
    const hasValidExt = ACCEPTED_EXTENSIONS.some(e => ext.endsWith(e));
    const hasValidMime = ACCEPTED_TYPES.includes(selected.type);
    if (!hasValidExt || !hasValidMime) {
      return 'Only PDF and Word documents are supported.';
    }
    if (selected.size > MAX_SIZE_MB * 1024 * 1024) {
      return `File size must be under ${MAX_SIZE_MB} MB.`;
    }
    return '';
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    const err = validateFile(selected);
    if (err) {
      setFile(null);
      setErrors(prev => ({ ...prev, file: err }));
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setFile(selected);
    setErrors(prev => ({ ...prev, file: '' }));
  };

  const handleRemove = () => {
    setFile(null);
    setErrors(prev => ({ ...prev, file: '' }));
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleSubmit = () => {
    const newErrors: Record<string, string> = {};
    if (!file) newErrors.file = 'Please upload your resume.';

    setErrors(newErrors);
    setTouched(prev => ({ ...prev, file: true }));

    if (Object.keys(newErrors).length > 0) return;

    const hasOptionalFields = jobRole.trim() && jobDescription.trim();
    if (!hasOptionalFields && !showHint) {
      setShowHint(true);
      return;
    }

    setShowHint(false);
    onSubmit({
      file: file!,
      jobRole: jobRole.trim(),
      jobDescription: jobDescription.trim()
    });
  };

  const handleClose = () => {
    setFile(null);
    setJobRole('');
    setJobDescription('');
    setErrors({});
    setTouched({});
    setShowHint(false);
    close();
  };

  return (
    <CommonDialog
      isOpen={isOpen}
      close={handleClose}
      dialogTitle='Boost ATS Score'
      width='w-full max-w-[520px]'
    >
      <div className='space-y-5'>
        <p className='text-xs text-gray-500'>
          Upload your resume and provide the job details to get started.
        </p>

        <div>
          <label className='text-sm font-semibold'>
            Upload Resume <span className='text-red-500'>*</span>
          </label>

          <input
            ref={inputRef}
            type='file'
            accept='.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            className='hidden'
            onChange={handleFileChange}
          />

          {!file ? (
            <div
              onClick={() => inputRef.current?.click()}
              className={`mt-1 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed bg-gamma p-6 transition hover:border-primary/40 ${
                errors.file && touched.file
                  ? 'border-red-400'
                  : 'border-[#E3E5E9]'
              }`}
            >
              <Upload size={24} className='mb-2 text-gray-400' />
              <p className='text-sm text-gray-500'>
                Click to upload{' '}
                <span className='font-medium text-primary'>PDF or Word</span>
              </p>
              <p className='mt-1 text-xs text-gray-400'>Max {MAX_SIZE_MB} MB</p>
            </div>
          ) : (
            <div className='mt-1 flex items-center justify-between rounded-lg border border-[#E3E5E9] bg-gamma px-4 py-3'>
              <div className='flex items-center gap-2 text-sm text-gray-700'>
                <i className='fa fa-file-pdf-o text-primary' />
                <span className='max-w-[260px] truncate'>{file.name}</span>
                <span className='text-xs text-gray-400'>
                  ({(file.size / 1024).toFixed(1)} KB)
                </span>
              </div>
              <div className='flex gap-2'>
                <button
                  onClick={handleRemove}
                  className='text-xs font-medium text-red-500 hover:underline'
                >
                  Remove
                </button>
                <button
                  onClick={() => inputRef.current?.click()}
                  className='text-xs font-medium text-primary hover:underline'
                >
                  Replace
                </button>
              </div>
            </div>
          )}

          {errors.file && touched.file && (
            <p className='mt-1 text-xs text-red-500'>{errors.file}</p>
          )}

          <div className='mt-2 flex items-start gap-1.5 text-[11px] text-gray-400'>
            <InfoIcon size={13} className='mt-[1px] shrink-0 text-gray-400' />
            <span>
              Documents in <strong className='text-gray-500'>.pdf</strong> and{' '}
              <strong className='text-gray-500'>.docx</strong> format are ATS
              friendly.
            </span>
          </div>
        </div>

        <div>
          <label className='text-sm font-semibold'>Job Role</label>
          <input
            type='text'
            value={jobRole}
            onChange={e => {
              setJobRole(e.target.value);
              if (showHint) setShowHint(false);
            }}
            placeholder='e.g. Frontend Developer'
            className='mt-1 w-full rounded-lg border border-[#E3E5E9] bg-gamma p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/10'
          />
        </div>

        <div>
          <label className='text-sm font-semibold'>Job Description</label>
          <textarea
            value={jobDescription}
            onChange={e => {
              setJobDescription(e.target.value);
              if (showHint) setShowHint(false);
            }}
            rows={4}
            placeholder='Paste the job description here...'
            className='mt-1 w-full resize-none rounded-lg border border-[#E3E5E9] bg-gamma p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/10'
          />
        </div>

        {showHint && (
          <div className='flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3'>
            <Info size={16} className='mt-0.5 flex-shrink-0 text-primary' />
            <p className='text-xs leading-relaxed text-blue-800'>
              Adding a job role and job description gives the best results.
              Click <span className='font-semibold'>Scan Resume</span> again to
              continue without them.
            </p>
          </div>
        )}

        <div className='flex justify-end pt-1'>
          <Button
            variant='contained'
            label='Scan Resume'
            handleClickBtn={handleSubmit}
            classes='!ml-0'
          />
        </div>
      </div>
    </CommonDialog>
  );
};

export default BoostATSModal;
